import { acceptHMRUpdate, defineStore } from 'pinia'
import { toRaw } from 'vue'
import { polygonCentroid } from 'd3'
import { useMaps } from './maps'

const DEFAULT_SIZE_PX = 40

/**
 * Icon (SVG/img URL) pinned on map, centered on polygon or point.
 *
 * @example
 * import myIcon from '@/assets/icons/my-icon.svg'
 * import { useMapSvgOverlaysStore } from '@/stores/mapSvgOverlays'
 *
 * const overlays = useMapSvgOverlaysStore()
 * overlays.addIcon({
 *   group: 'myPolygons',
 *   ring: polygonCoordinates[0], // GeoJSON outer ring [lng, lat][]
 *   iconUrl: myIcon,
 *   sizePx: 40,
 * })
 * // another polygon, another icon:
 * overlays.addIcon({ group: 'myPolygons', ring: otherRing, iconUrl: otherIcon })
 * overlays.clearGroup('myPolygons')
 */
export const useMapSvgOverlaysStore = defineStore('mapSvgOverlays', {
    state: () => ({
        overlaysByGroup: {},
        svgOverlayClass: null,
    }),
    actions: {
        getSvgOverlayClass() {
            if (this.svgOverlayClass) return this.svgOverlayClass

            const ms = useMaps()
            if (!ms.google || !ms.google.maps || !ms.google.maps.OverlayView) return null

            this.svgOverlayClass = class SvgOverlay extends ms.google.maps.OverlayView {
                constructor(position, iconUrl, sizePx, options) {
                    super()
                    this.position = position
                    this.iconUrl = iconUrl
                    this.sizePx = sizePx
                    this.opacity = options?.opacity ?? 1
                    // below floatPane so InfoWindows stay on top, still receives DOM events
                    this.pane = options?.pane || 'overlayMouseTarget'
                    this.className = options?.className || ''
                    this.onClick = options?.onClick || null
                    this.div = null
                }
                onAdd() {
                    const div = document.createElement('div')
                    div.style.position = 'absolute'
                    div.style.transform = 'translate(-50%, -50%)'
                    div.style.pointerEvents = this.onClick ? 'auto' : 'none'
                    if (this.onClick) {
                        div.style.cursor = 'pointer'
                        div.addEventListener('click', (event) => {
                            event.stopPropagation()
                            this.onClick(this.position)
                        })
                    }
                    div.style.width = `${this.sizePx}px`
                    div.style.height = `${this.sizePx}px`
                    div.style.opacity = String(this.opacity)
                    if (this.className) div.className = this.className

                    const img = document.createElement('img')
                    img.src = this.iconUrl
                    img.alt = ''
                    img.draggable = false
                    img.style.width = '100%'
                    img.style.height = '100%'
                    img.style.display = 'block'

                    div.appendChild(img)
                    this.div = div

                    const panes = this.getPanes()
                    const targetPane = panes?.[this.pane] || panes?.floatPane || panes?.overlayLayer
                    targetPane?.appendChild(div)
                }
                draw() {
                    if (!this.div) return
                    const projection = this.getProjection()
                    if (!projection) return
                    const point = projection.fromLatLngToDivPixel(this.position)
                    if (!point) return
                    this.div.style.left = `${point.x}px`
                    this.div.style.top = `${point.y}px`
                }
                onRemove() {
                    if (this.div?.parentNode) {
                        this.div.parentNode.removeChild(this.div)
                    }
                    this.div = null
                }
            }

            return this.svgOverlayClass
        },
        /** GeoJSON ring `[lng, lat][]` → `{ lat, lng }` centroid */
        ringCentroidLatLng(ring) {
            if (!Array.isArray(ring) || ring.length < 3) return null

            const first = ring[0]
            const last = ring[ring.length - 1]
            const closed = Array.isArray(first) && Array.isArray(last)
                && first[0] === last[0] && first[1] === last[1]
            // if polygon is closed, remove the last point to avoid double point
            const points = closed ? ring.slice(0, -1) : ring
            if (points.length < 3) return null

            // calculate the center of the polygon
            const [cx, cy] = polygonCentroid(points)
            if (!Number.isFinite(cx) || !Number.isFinite(cy)) return null
            return { lat: cy, lng: cx }
        },
        /**
         * Add a map icon at polygon centroid or fixed position. Remove with `clearGroup(group)`.
         *
         * @param {string} opts.group - Group id for `clearGroup`
         * @param {string} opts.iconUrl - Imported asset URL
         * @param {Array<[number, number]>} [opts.ring] - Polygon outer ring `[lng, lat][]` (icon at centroid)
         * @param {{ lat: number, lng: number }|google.maps.LatLng} [opts.position] - Fixed position if no `ring`
         * @param {number} [opts.sizePx=40] - Icon size in px
         * @param {object} [opts.options] - `{ opacity?, pane?, className?, parkingLotId?, onClick? }`
         * @returns {google.maps.OverlayView|null}
         */
        addIcon({
            group,
            iconUrl,
            ring,
            position,
            sizePx = DEFAULT_SIZE_PX,
            options = {}
        }) {
            if (!group || !iconUrl) return null

            const posFromRing = ring ? this.ringCentroidLatLng(ring) : null
            const coords = position ?? posFromRing
            if (!coords) return null

            const ms = useMaps()
            if (!ms.map || !ms.google) return null

            const latLng = coords instanceof ms.google.maps.LatLng
                ? coords
                : new ms.google.maps.LatLng(coords.lat, coords.lng)

            const OverlayClass = this.getSvgOverlayClass()
            if (!OverlayClass) return null

            const overlay = new OverlayClass(latLng, iconUrl, sizePx, options)
            overlay.setMap(toRaw(ms.map))

            const raw = toRaw(overlay)
            raw.parkingLotId = options.parkingLotId ?? null
            if (!this.overlaysByGroup[group]) this.overlaysByGroup[group] = []
            this.overlaysByGroup[group].push(raw)
            return raw
        },
        removeByParkingLotId(group, parkingLotId) {
            if (!group || parkingLotId == null) return

            const list = this.overlaysByGroup[group]
            if (!Array.isArray(list)) return

            this.overlaysByGroup[group] = list.filter(overlay => {
                const raw = toRaw(overlay)
                if (raw?.parkingLotId === parkingLotId) {
                    raw?.setMap?.(null)
                    return false
                }
                return true
            })
        },
        removeUntaggedInGroup(group) {
            const list = this.overlaysByGroup[group]
            if (!Array.isArray(list)) return

            this.overlaysByGroup[group] = list.filter(overlay => {
                const raw = toRaw(overlay)
                if (raw?.parkingLotId == null) {
                    raw?.setMap?.(null)
                    return false
                }
                return true
            })
        },
        clearGroup(group) {
            const list = this.overlaysByGroup[group]
            if (!Array.isArray(list)) return
            list.forEach(overlay => {
                const raw = toRaw(overlay)
                if (raw && typeof raw.setMap === 'function') raw.setMap(null)
            })
            this.overlaysByGroup[group] = []
        },
        clearAll() {
            Object.keys(this.overlaysByGroup).forEach(group => this.clearGroup(group))
            this.overlaysByGroup = {}
        },
    },
})

if (import.meta.hot) {
    import.meta.hot.accept(acceptHMRUpdate(useMapSvgOverlaysStore, import.meta.hot))
}
